import type { User } from 'firebase/auth'
import type { UserProfile } from '@domain/models/user'
import { AuthFeature } from '@domain/features/auth/auth-feature'
import { AuthenticationService } from '@core/services/authentication/authentication-service'
import { ImageService } from '@core/services/image/image-service'
import { UserService } from '@core/services/user/user-service'
import { CoreError, CoreErrorCause } from '@domain/models/core-error'

const userService = UserService.getInstance()
const imageService = ImageService.getInstance()

export interface AuthenticatedUser {
  uid: string
  email: string
  avatarUrl?: string | null
  fullName?: string | null
  profileSlug?: string | null
  isAdmin: boolean
}

export function isAdmin(
  user: Pick<UserProfile, 'admin' | 'isAdmin'> | null | undefined,
): boolean {
  return user?.admin === true || user?.isAdmin === true
}

function mapUser(authUser: User, userProfile: UserProfile | null): AuthenticatedUser {
  const profileSlug = userProfile?.profile_slug?.trim()
  return {
    uid: userProfile?.uid || authUser.uid,
    email: userProfile?.email || authUser.email || '',
    avatarUrl: userProfile?.avatarUrl || authUser.photoURL,
    fullName: userProfile?.fullName || authUser.displayName,
    profileSlug: profileSlug && profileSlug.length > 0 ? profileSlug : null,
    isAdmin: isAdmin(userProfile),
  }
}

export class UserFeature {
  private static getAuthUserIdOrThrow(): string {
    const userId = AuthenticationService.getInstance().userId
    if (!userId) {
      throw new CoreError({
        cause: CoreErrorCause.Forbidden,
        context: 'No authenticated user available for user feature.',
      })
    }
    return userId
  }

  public static async loadCurrentUser(): Promise<UserProfile> {
    const userId = UserFeature.getAuthUserIdOrThrow()
    return userService.loadUser(userId)
  }

  public static async loadUserById(userId: string): Promise<UserProfile> {
    return userService.loadUser(userId)
  }

  public static async saveName(fullName: string): Promise<void> {
    const userId = UserFeature.getAuthUserIdOrThrow()
    await userService.updateUserName(userId, fullName.trim())
  }

  public static async saveProfilePhoto(photoUrl: string): Promise<string> {
    const userId = UserFeature.getAuthUserIdOrThrow()
    await userService.updateUserProfilePhoto(userId, photoUrl)
    return photoUrl
  }

  public static async uploadProfilePhoto(file: File): Promise<string> {
    const userId = UserFeature.getAuthUserIdOrThrow()
    const photoUrl = await imageService.uploadUserProfileImage(userId, file)
    await userService.updateUserProfilePhoto(userId, photoUrl)
    return photoUrl
  }

  public static async deleteCurrentUser(): Promise<void> {
    const userId = UserFeature.getAuthUserIdOrThrow()
    await userService.deleteUser(userId)
  }

  public static async deleteUser(currentPassword: string): Promise<void> {
    try {
      if (!currentPassword.trim()) {
        throw new CoreError({
          cause: CoreErrorCause.InvalidCredential,
          context: 'Current password is required to delete account.',
        })
      }

      const userId = UserFeature.getAuthUserIdOrThrow()
      await AuthFeature.reauthenticate(currentPassword)
      await userService.deleteUser(userId)
      await AuthFeature.deleteUser()
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'User deletion failed.',
        underlyingException: error,
      })
    }
  }

  public static observeAuthenticatedUser(
    callback: (user: AuthenticatedUser | null) => void,
    onError?: (error: Error) => void,
  ): () => void {
    let unsubscribeUser: (() => void) | null = null

    const unsubscribeAuth = AuthFeature.observeAuthSession((authUser) => {
      if (unsubscribeUser) {
        unsubscribeUser()
        unsubscribeUser = null
      }

      if (!authUser) {
        callback(null)
        return
      }

      unsubscribeUser = userService.observeUser(
        authUser.uid,
        (userProfile) => {
          callback(mapUser(authUser, userProfile))
        },
        (error) => {
          onError?.(error)
          callback(mapUser(authUser, null))
        },
      )
    })

    return () => {
      if (unsubscribeUser) {
        unsubscribeUser()
      }
      unsubscribeAuth()
    }
  }
}

export function observeAuthenticatedUser(
  callback: (user: AuthenticatedUser | null) => void,
  onError?: (error: Error) => void,
) {
  return UserFeature.observeAuthenticatedUser(callback, onError)
}

export async function loadCurrentUser(): Promise<UserProfile> {
  return UserFeature.loadCurrentUser()
}

export async function loadUserById(userId: string): Promise<UserProfile> {
  return UserFeature.loadUserById(userId)
}

export async function saveName(fullName: string): Promise<void> {
  return UserFeature.saveName(fullName)
}

export async function saveProfilePhoto(photoUrl: string): Promise<string> {
  return UserFeature.saveProfilePhoto(photoUrl)
}

export async function uploadProfilePhoto(file: File): Promise<string> {
  return UserFeature.uploadProfilePhoto(file)
}

export async function deleteCurrentUser(): Promise<void> {
  return UserFeature.deleteCurrentUser()
}

export async function deleteUser(currentPassword: string): Promise<void> {
  return UserFeature.deleteUser(currentPassword)
}
